import { Modal, Text, Loader, Divider } from '@mantine/core'

import { useQuery, useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  APPLICANT_STEP_SYNC_PREVIEW,
  SYNC_APPLICANT_STEPS_TO_TEMPLATE,
} from 'src/graphql/steps.graphql'
import { formatStepType } from 'src/lib/formatters'

interface SyncStepItem {
  stepType: string
  customStepId?: string | null
  label?: string | null
}

interface SyncStepsModalProps {
  applicantId: string
  isOpen: boolean
  onClose: () => void
  refetchQueries?: any
}

const SyncStepsModal = ({
  applicantId,
  isOpen,
  onClose,
  refetchQueries,
}: SyncStepsModalProps) => {
  const labelFor = (item: SyncStepItem) =>
    item.customStepId
      ? item.label ?? 'Custom step'
      : formatStepType(item.stepType as any)

  const { data, loading } = useQuery(APPLICANT_STEP_SYNC_PREVIEW, {
    variables: { applicantId },
    skip: !isOpen,
    fetchPolicy: 'network-only',
    onError: () => toast('Unable to load step changes', 'error'),
  })

  const [syncSteps, { loading: applying }] = useMutation(
    SYNC_APPLICANT_STEPS_TO_TEMPLATE,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Steps synced', 'success')
        onClose()
      },
      onError: () => toast('Unable to sync steps', 'error'),
    }
  )

  const toAdd: SyncStepItem[] = data?.applicantStepSyncPreview?.toAdd ?? []
  const toRemove: SyncStepItem[] =
    data?.applicantStepSyncPreview?.toRemove ?? []
  const noChanges = !loading && toAdd.length === 0 && toRemove.length === 0

  const renderList = (items: SyncStepItem[]) => (
    <div className="flex flex-col gap-1">
      {items.map((item) => (
        <Text
          key={item.customStepId ?? item.stepType}
          className="text-sm text-doubleNickel-gray-800"
        >
          {labelFor(item)}
        </Text>
      ))}
    </div>
  )

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Sync steps to the job's template"
      size="lg"
      overlayProps={{ backgroundOpacity: 0.2, blur: 5 }}
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader size="sm" color="gray" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {noChanges ? (
            <Text className="text-sm text-doubleNickel-gray-600">
              This applicant&apos;s steps already match the template. No changes
              to apply.
            </Text>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <Text className="text-xs font-semibold uppercase text-doubleNickel-gray-500">
                  Steps to add ({toAdd.length})
                </Text>
                {toAdd.length > 0 ? (
                  renderList(toAdd)
                ) : (
                  <Text className="text-sm text-doubleNickel-gray-500">
                    None
                  </Text>
                )}
              </div>
              <Divider />
              <div className="flex flex-col gap-2">
                <Text className="text-xs font-semibold uppercase text-doubleNickel-gray-500">
                  Steps to remove ({toRemove.length})
                </Text>
                {toRemove.length > 0 ? (
                  renderList(toRemove)
                ) : (
                  <Text className="text-sm text-doubleNickel-gray-500">
                    None
                  </Text>
                )}
              </div>
            </>
          )}

          <Divider />
          <Text className="text-xs text-doubleNickel-gray-500">
            Steps are added as Not Started and removed steps are only
            deactivated — their documents stay in the full documents list. Other
            steps&apos; statuses are not changed. This might trigger automations
            that match these steps.
          </Text>

          <div className="flex flex-row justify-end gap-2">
            <Button text="Cancel" variant="outline" onClick={onClose} />
            <Button
              text="Apply"
              onClick={() => syncSteps({ variables: { applicantId } })}
              loading={applying}
              disabled={noChanges}
            />
          </div>
        </div>
      )}
    </Modal>
  )
}

export default SyncStepsModal
